// Chats (surface 7, module 09 M1 to M6, M11; U7, U20). The artboard's shape: a list on the
// left with red unread counts, the conversation on the right, flat with quote-reply (M5), a
// screenshot pasted straight into the composer (M2), edit and delete on your own messages (M6),
// groups and external chats with guests invited by link (M3), and Cobrain's proposed link to a
// matter, a deal or a company shown for confirmation and never forced (M1).
//
// Routed by App.tsx as /chats and /chats/:id (the main session's lines; HANDOVER-messenger.md).
// The same pane serves a guest (MessengerGuest.tsx) with no store and without the colleague-only
// actions, so what a guest sees is what the API returns to a guest and nothing drawn here.
//
// Three honesty rules: a message shows what the API returned, as typed (the corpus copy is
// scanned, the view is not, module 09 DESIGN); a removed message is drawn only for its sender,
// as removed, because nobody else receives it; a failed call is the API's own words.
import { flushSync } from 'react-dom';
import { EmojiButton } from './chats/EmojiButton.js';
import { ActionButton, ActionInput, ActionTextarea, CardFrame, Chip, CompanyName, List, ListRow, OptionButton, SectionHeading } from './parts/index.js';
import { useViewing } from './view-as.js';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { reReadOnReconnect, useLiveRefresh } from './live.js';
import { ApiError, Unauthenticated, type Store } from './api.js';
import {
  messenger, type ConversationRow, type GuestView, type LinkCandidate, type LinkView, type Me, type MessageView, type MessengerEvent, type PersonPick, type ProposeOutcome,
} from './messenger/api.js';
import { dayWords, initials, mentionSpans, sameDay, sizeWords, whenWords } from './messenger/format.js';
import { useMessengerSocket } from './messenger/ws.js';
import './messenger.css';

function messageOf(e: unknown): string {
  if (e instanceof Unauthenticated) throw e;
  if (e instanceof ApiError) return e.message;
  return e instanceof Error ? e.message : 'something went wrong';
}

/** The red count for the left menu (M4): polled, and moved at once by the socket. Exported for App.tsx's rail. */
export function useChatUnread(store: string | null, enabled = true): number {
  const [total, setTotal] = useState(0);
  const read = useCallback(() => {
    if (!enabled) return;
    messenger.unread(store).then((r) => setTotal(r.unread.total)).catch(() => {});
  }, [store, enabled]);
  useEffect(() => { read(); const t = window.setInterval(read, 30_000); return () => window.clearInterval(t); }, [read]);
  useMessengerSocket(enabled ? store : null, (e) => { if (e.type === 'message' || e.type === 'read' || e.type === 'delete') read(); });
  return total;
}

// -------------------------------------------------------------------------------------------
// The list
// -------------------------------------------------------------------------------------------

function Unread({ n }: { n: number }) {
  return n > 0 ? <Chip className="unread" aria-label={`${n} unread`}>{n > 99 ? '99+' : n}</Chip> : null;
}

function ChatList({ rows, selected, onOpen, onNew, canStart }: { rows: ConversationRow[]; selected: string | null; onOpen: (id: string) => void; onNew: () => void; canStart: boolean }) {
  const total = rows.reduce((n, c) => n + c.unread, 0);
  return (
    <div className="chat-list">
      <div className="chat-list-head">
        <p>Chats <Unread n={total} /></p>
        {canStart ? <ActionButton type="button" className="chat-new" onClick={onNew} title="Start a chat">+ New</ActionButton> : null}
      </div>
      {rows.length === 0 ? <p className="note" style={{ margin: 'var(--pad-y) var(--gap)' }}>{canStart ? 'No chats yet. Start one with a colleague, or a group.' : 'No chats yet.'}</p> : null}
      <List className="chat-choices">{rows.map((c) => (
        <ListRow key={c.id} className="chat-choice"><ActionButton reading type="button" className={c.id === selected ? 'choice on' : 'choice'} onClick={() => onOpen(c.id)}>
          <span className="choice-name">
            {c.name}
            {c.isExternal ? <Chip className="guest">guests</Chip> : c.kind === 'group' ? <Chip className="quiet">group</Chip> : null}
          </span>
          {c.lastMessagePreview ? <span className="choice-preview">{c.lastMessagePreview}</span> : null}
          <Unread n={c.unread} />
        </ActionButton></ListRow>
      ))}</List>
      <p className="note" style={{ margin: 'var(--pad-y) var(--gap) 0' }}>Direct messages notify at once; groups on mention.</p>
    </div>
  );
}

// -------------------------------------------------------------------------------------------
// One message
// -------------------------------------------------------------------------------------------

function Body({ text, names }: { text: string; names: string[] }) {
  const spans = useMemo(() => mentionSpans(text, names), [text, names]);
  return <span className="msg-body">{spans.map((s, i) => (s.kind === 'mention' ? <mark key={i} className="mention">{s.text}</mark> : <span key={i}>{s.text}</span>))}</span>;
}

function Bubble({ m, store, names, onQuote, onEdit, onDelete }: { m: MessageView; store: string | null; names: string[]; onQuote: (m: MessageView) => void; onEdit: (m: MessageView) => void; onDelete: (m: MessageView) => void }) {
  if (m.deletedAt) {
    return <CardFrame variant="panel" className={m.mine ? 'msg me removed' : 'msg removed'}>You removed a message · kept in the record</CardFrame>;
  }
  return (
    <CardFrame variant="panel" className={m.mine ? 'msg me' : 'msg'}>
      <p className="from">{m.mine ? 'You' : m.senderName} · {whenWords(m.sentAt)}{m.editedAt ? ' · edited' : ''}</p>
      {m.quoted ? <p className="qr">{m.quoted.senderName}: {m.quoted.deletedAt ? <em>message removed</em> : m.quoted.body.slice(0, 200)}</p> : null}
      <Body text={m.body} names={names} />
      {m.files.length > 0 ? (
        <div className="msg-files">
          {m.files.map((f) => f.isImage
            ? <a key={f.attachmentId} href={messenger.fileUrl(store, m.id, f.attachmentId)} target="_blank" rel="noreferrer"><img className="msg-image" src={messenger.fileUrl(store, m.id, f.attachmentId)} alt={f.name} loading="lazy" /></a>
            : <a key={f.attachmentId} className="msg-file" href={messenger.fileUrl(store, m.id, f.attachmentId)} target="_blank" rel="noreferrer">{f.name} <span className="note">{sizeWords(f.sizeBytes)}</span></a>)}
        </div>
      ) : null}
      <span className="msg-acts">
        <ActionButton type="button" className="dotted" onClick={() => onQuote(m)}>Reply</ActionButton>
        {m.mine ? <ActionButton type="button" className="dotted" onClick={() => onEdit(m)}>Edit</ActionButton> : null}
        {m.mine ? <ActionButton type="button" className="dotted" onClick={() => onDelete(m)}>Delete</ActionButton> : null}
      </span>
    </CardFrame>
  );
}

// -------------------------------------------------------------------------------------------
// Cobrain's proposal and the link (M1)
// -------------------------------------------------------------------------------------------

function linkWords(l: LinkView): string {
  return l.kind === 'matter' ? `${l.title}${l.organisationName ? ` (${l.organisationName})` : ''}` : l.title;
}

function LinkLine({ link, canEdit, onChange }: { link: LinkView | null; canEdit: boolean; onChange: () => void }) {
  if (!link || (link.proposedBy === 'model' && !link.confirmedAt)) {
    return canEdit ? <span className="note">· not linked · <ActionButton type="button" className="dotted" onClick={onChange}>link to a matter, a deal or a company</ActionButton></span> : null;
  }
  return <span className="note">· linked to <em>{link.kind === 'organisation' ? <CompanyName id={link.id} name={link.title} /> : linkWords(link)}</em>{canEdit ? <> · <ActionButton type="button" className="dotted" onClick={onChange}>change</ActionButton></> : null}</span>;
}

function Proposal({ link, busy, onAnswer }: { link: LinkView; busy: boolean; onAnswer: (a: 'accept' | 'dismiss') => void }) {
  return (
    <div className="changed proposal">
      <strong>Cobrain:</strong> this looks like {link.kind === 'matter' ? 'the matter' : 'the company'} <em>{linkWords(link)}</em>. Link it?{' '}
      <ActionButton type="button" className="dotted" disabled={busy} onClick={() => onAnswer('accept')}>Yes</ActionButton> · <ActionButton type="button" className="dotted" disabled={busy} onClick={() => onAnswer('dismiss')}>No</ActionButton>
      {link.reason ? <p className="note" style={{ margin: 'calc(var(--pad-y) / 2) 0 0' }}>{link.reason}</p> : null}
    </div>
  );
}

export function LinkPanel({ store, conversationId, current, laneAvailable, onClose, onChanged }: { store: string | null; conversationId: string; current: LinkView | null; laneAvailable: boolean; onClose: () => void; onChanged: (l: LinkView | null) => void }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<LinkCandidate[]>([]);
  // BUGS.md M27: the read keeps twelve of each kind and says when either had more (Mike by pop-up, 6 Oct 2026, "Say more
  // match (Recommended)": "When more than twelve match, a line under the list reads "More match. Type more of the name."").
  const [more, setMore] = useState(false);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  useEffect(() => {
    if (q.trim().length < 2) { setRows([]); setMore(false); return; }
    const t = window.setTimeout(() => { messenger.linkCandidates(store, q).then((r) => { setRows(r.candidates); setMore(r.more === true); }).catch((e: unknown) => setSaid(messageOf(e))); }, 200);
    return () => window.clearTimeout(t);
  }, [q, store]);
  const set = async (body: { kind: 'matter' | 'organisation' | 'none'; id?: string }) => {
    setBusy(true); setSaid(null);
    try { const r = await messenger.setLink(store, conversationId, body); onChanged(r.link); onClose(); } catch (e) { setSaid(messageOf(e)); } finally { setBusy(false); }
  };
  const propose = async () => {
    setBusy(true); setSaid(null);
    try {
      const o: ProposeOutcome = await messenger.propose(store, conversationId);
      if (o.outcome === 'proposed') { onChanged(o.link); onClose(); }
      else if (o.outcome === 'none') setSaid(`Cobrain read the chat and proposes nothing: ${o.reason}`);
      else if (o.outcome === 'refused') setSaid(`Not today: the store has spent $${o.spentUsd.toFixed(2)} of its $${o.budgetUsd.toFixed(2)} daily model budget.`);
      else if (o.outcome === 'capped') setSaid(`Not today: ${o.today} of ${o.dailyMax} proposals already made in this store.`);
      else if (o.outcome === 'failed') setSaid(`Cobrain could not read the chat: ${o.error}`);
      else setSaid(o.outcome === 'nothing_to_read' ? 'Nothing to read yet.' : o.outcome === 'no_candidates' ? 'No open matter or company to choose from.' : o.outcome === 'already_proposed' ? 'A proposal is already waiting above.' : o.outcome === 'already_linked' ? 'This chat is linked already.' : 'Not possible here.');
    } catch (e) { setSaid(messageOf(e)); } finally { setBusy(false); }
  };
  return (
    <CardFrame variant="panel" className="link-panel">
      <h2>Link this chat</h2>
      <p className="rule">To a matter, a deal (a kind of matter) or a company. Cobrain can propose one from what was said; you decide.</p>
      {current && current.confirmedAt ? <p className="note">Linked to <em>{linkWords(current)}</em>. <ActionButton type="button" className="dotted" disabled={busy} onClick={() => void set({ kind: 'none' })}>Unlink</ActionButton></p> : null}
      <div className="link-row">
        <span className="field wide"><ActionInput reading value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search matters, deals and companies…" autoFocus /></span>
        {laneAvailable ? <ActionButton type="button" className="btn small quiet" disabled={busy} onClick={() => void propose()}>Ask Cobrain</ActionButton> : null}
      </div>
      {rows.length > 0 ? (
        <div className="opts" style={{ marginTop: 'var(--pad-y)' }}>
          {rows.map((c) => <OptionButton key={`${c.kind}:${c.id}`} type="button" disabled={busy} onClick={() => void set({ kind: c.kind, id: c.id })} note={c.detail}>{c.title}</OptionButton>)}
        </div>
      ) : q.trim().length >= 2 ? <p className="note" style={{ marginTop: 'var(--pad-y)' }}>Nothing by that name that you can see.</p> : null}
      {rows.length > 0 && more ? <p className="note link-more" style={{ marginTop: 'var(--gap)' }}>More match. Type more of the name.</p> : null}
      {said ? <p className="note" style={{ marginTop: 'var(--pad-y)' }}>{said}</p> : null}
      <p className="note" style={{ marginTop: 'calc(var(--pad-x) - var(--r-sm))' }}><ActionButton reading type="button" className="dotted" onClick={onClose}>Close</ActionButton></p>
    </CardFrame>
  );
}

// -------------------------------------------------------------------------------------------
// New chat, invite a guest
// -------------------------------------------------------------------------------------------

export function NewChat({ store, onClose, onMade }: { store: string | null; onClose: () => void; onMade: (c: ConversationRow) => void }) {
  const [people, setPeople] = useState<PersonPick[]>([]);
  // How many colleagues hold an account and have never signed in. The picker can only offer people who
  // have, so without this the panel says "No colleagues yet" while Cobrain knows ten of them (M17).
  const [invited, setInvited] = useState(0);
  const [kind, setKind] = useState<'direct' | 'group'>('direct');
  const [title, setTitle] = useState('');
  const [external, setExternal] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  useEffect(() => { messenger.people(store).then((r) => { setPeople(r.people); setInvited(r.invited); }).catch((e: unknown) => setSaid(messageOf(e))); }, [store]);
  const toggle = (id: string) => setPicked((p) => (kind === 'direct' ? [id] : p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const make = async () => {
    setBusy(true); setSaid(null);
    try {
      const r = await messenger.create(store, { kind, ...(kind === 'group' ? { title, isExternal: external } : {}), participantIds: picked });
      onMade(r.conversation);
    } catch (e) { setSaid(messageOf(e)); } finally { setBusy(false); }
  };
  return (
    <CardFrame variant="panel" className="new-chat">
      <h2>New chat</h2>
      <div className="filters" style={{ margin: 'var(--gap) 0 calc(var(--pad-x) - var(--r-sm))' }}>
        <ActionButton type="button" className={kind === 'direct' ? 'tg on' : 'tg'} onClick={() => { setKind('direct'); setPicked((p) => p.slice(0, 1)); }}>Direct</ActionButton>
        <ActionButton type="button" className={kind === 'group' ? 'tg on' : 'tg'} onClick={() => setKind('group')}>Group</ActionButton>
      </div>
      {kind === 'group' ? (
        <div className="new-chat-fields">
          <span className="field wide"><ActionInput value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Group name" maxLength={120} /></span>
          <label className="note new-chat-ext"><ActionInput type="checkbox" checked={external} onChange={(e) => setExternal(e.target.checked)} /> External: guests may be invited by link</label>
        </div>
      ) : <p className="note">One colleague. A guest joins an external group, not a direct chat.</p>}
      <div className="opts" style={{ marginTop: 'var(--pad-y)' }}>
        {people.map((p) => <OptionButton key={p.principalId} type="button" variant={picked.includes(p.principalId) ? 'lead' : 'plain'} onClick={() => toggle(p.principalId)}>{p.name}</OptionButton>)}
        {people.length === 0 ? (
          <span className="note">
            {invited > 0
              ? invited === 1
                ? 'One colleague has been invited to Cobrain and has not signed in yet. They can be picked here once they do.'
                : `${invited} colleagues have been invited to Cobrain and none has signed in yet. They can be picked here once they do.`
              : 'Nobody else has an account on this store yet.'}
          </span>
        ) : null}
      </div>
      {said ? <p className="note" style={{ marginTop: 'var(--pad-y)' }}>{said}</p> : null}
      <p style={{ margin: 'var(--pad-x) 0 0', display: 'flex', gap: 'var(--pad-y)' }}>
        <ActionButton type="button" className="btn small" disabled={busy || (kind === 'direct' ? picked.length !== 1 : !title.trim())} onClick={() => void make()}>Start</ActionButton>
        <ActionButton reading type="button" className="btn small quiet" onClick={onClose}>Cancel</ActionButton>
      </p>
    </CardFrame>
  );
}

function InviteGuest({ store, conversationId, guests, onClose, onInvited, onRevoked }: { store: string | null; conversationId: string; guests: GuestView[]; onClose: () => void; onInvited: () => void; onRevoked: () => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const [link, setLink] = useState<string | null>(null);
  const invite = async () => {
    setBusy(true); setSaid(null);
    try {
      const r = await messenger.inviteGuest(store, conversationId, { displayName: name, ...(email.trim() ? { email: email.trim() } : {}), ...(phone.trim() ? { e164: phone.trim() } : {}) });
      setLink(r.inviteUrl); onInvited();
    } catch (e) { setSaid(messageOf(e)); } finally { setBusy(false); }
  };
  const revoke = async (id: string) => { setBusy(true); try { await messenger.revokeGuest(store, id); onRevoked(); } catch (e) { setSaid(messageOf(e)); } finally { setBusy(false); } };
  return (
    <CardFrame variant="panel" className="new-chat">
      <h2>Guests</h2>
      <p className="rule">A guest has their own identity and sees this chat alone. Cobrain sends nothing: you copy the link and send it yourself. The link is shown once.</p>
      {guests.length > 0 ? (
        <ul className="guest-list">
          {guests.map((g) => <li key={g.id}><span>{g.displayName} <span className="note">{g.email ?? g.e164}{g.revokedAt ? ' · revoked' : g.acceptedAt ? ' · joined' : ' · invited'}</span></span>{!g.revokedAt ? <ActionButton type="button" className="dotted" disabled={busy} onClick={() => void revoke(g.id)}>Revoke</ActionButton> : null}</li>)}
        </ul>
      ) : null}
      {link ? <p className="invite-link"><strong>Invite link, shown once:</strong> <code>{link}</code> <ActionButton type="button" className="dotted" onClick={() => { void navigator.clipboard?.writeText(link); }}>Copy</ActionButton></p> : (
        <div className="new-chat-fields">
          <span className="field wide"><ActionInput value={name} onChange={(e) => setName(e.target.value)} placeholder="Guest's name" maxLength={120} /></span>
          <span className="field wide"><ActionInput value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" type="email" /></span>
          <span className="field wide"><ActionInput value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="or phone, +44…" /></span>
        </div>
      )}
      {said ? <p className="note" style={{ marginTop: 'var(--pad-y)' }}>{said}</p> : null}
      <p style={{ margin: 'var(--pad-x) 0 0', display: 'flex', gap: 'var(--pad-y)' }}>
        {!link ? <ActionButton type="button" className="btn small" disabled={busy || !name.trim() || (!email.trim() && !phone.trim())} onClick={() => void invite()}>Invite</ActionButton> : null}
        <ActionButton reading type="button" className="btn small quiet" onClick={onClose}>Close</ActionButton>
      </p>
    </CardFrame>
  );
}

// -------------------------------------------------------------------------------------------
// The conversation
// -------------------------------------------------------------------------------------------

interface Pending { key: string; name: string; mimeType: string | null; preview: string | null }

export function ChatView({ store, me, id, onChanged, canManage, onBack }: { store: string | null; me: Me; id: string; onChanged: () => void; canManage: boolean; onBack?: () => void }) {
  const viewing = useViewing();
  const [conv, setConv] = useState<ConversationRow | null>(null);
  const [guests, setGuests] = useState<GuestView[]>([]);
  const [laneAvailable, setLaneAvailable] = useState(false);
  const [msgs, setMsgs] = useState<MessageView[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [quoting, setQuoting] = useState<MessageView | null>(null);
  const [editing, setEditing] = useState<MessageView | null>(null);
  const [pending, setPending] = useState<Pending[]>([]);
  const [busy, setBusy] = useState(false);
  const [panel, setPanel] = useState<'none' | 'link' | 'guests'>('none');
  const [proposalBusy, setProposalBusy] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const emojiCursor = useRef({ start: 0, end: 0 });
  const focusComposer = () => {
    composerRef.current?.focus();
    composerRef.current?.setSelectionRange(emojiCursor.current.start, emojiCursor.current.end);
  };

  const load = useCallback(async () => {
    setError(null);
    try {
      const [c, m] = await Promise.all([messenger.conversation(store, id), messenger.messages(store, id)]);
      setConv(c.conversation); setGuests(c.guests); setLaneAvailable(c.linkLaneAvailable); setMsgs(m.messages); setHasMore(m.hasMore);
      const last = m.messages.filter((x) => !x.deletedAt).at(-1);
      if (last && c.conversation.unread > 0) { await messenger.read(store, id, last.id); onChanged(); }
    } catch (e) { setError(messageOf(e)); }
  }, [store, id, onChanged]);
  useEffect(() => { setMsgs([]); setQuoting(null); setEditing(null); setPending([]); setPanel('none'); void load(); }, [load]);
  // Moves to the foot for a new message, and NOT while somebody is reading back through the
  // conversation (live.ts point 3). It matters more now than it did: before B1470 this effect only ever
  // ran on a socket event, and it now also runs after a reconnect re-read and after a return to the
  // window, either of which could arrive while they are twenty messages up.
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 120 || el.scrollTop === 0) el.scrollTo({ top: el.scrollHeight });
  }, [msgs.length]);

  const names = useMemo(() => (conv?.participants ?? []).map((p) => p.name), [conv]);

  // A socket event carries the view the SENDER'S request built, and `mine` is per requester:
  // the viewer decides it again from who sent the message (found by driving two browsers on
  // 2026-09-17: a colleague's message arrived drawn as "You").
  const asSeenByMe = useCallback((m: MessageView): MessageView => ({
    ...m, mine: (m.senderPrincipalId !== null && m.senderPrincipalId === me.principalId) || (m.senderGuestId !== null && m.senderGuestId === me.guestId),
  }), [me.principalId, me.guestId]);
  const onEvent = useCallback((e: MessengerEvent) => {
    if (!('conversationId' in e) || e.conversationId !== id) return;
    if (e.type === 'message') {
      const incoming = asSeenByMe(e.message);
      setMsgs((m) => (m.some((x) => x.id === incoming.id) ? m : [...m, incoming]));
      if (!incoming.mine && document.visibilityState === 'visible') { void messenger.read(store, id, incoming.id).then(onChanged).catch(() => {}); }
    } else if (e.type === 'edit') setMsgs((m) => m.map((x) => (x.id === e.message.id ? asSeenByMe(e.message) : x)));
    else if (e.type === 'delete') setMsgs((m) => m.filter((x) => x.id !== e.messageId).concat(m.filter((x) => x.id === e.messageId && x.mine).map((x) => ({ ...x, deletedAt: e.deletedAt, body: '', files: [] }))).sort((a, b) => a.sentAt.localeCompare(b.sentAt)));
    else if (e.type === 'link') setConv((c) => (c ? { ...c, link: e.link } : c));
    else if (e.type === 'conversation') void load();
  }, [id, store, load, onChanged, asSeenByMe]);
  /**
   * WHAT IS MISSED WHILE THE SOCKET IS DOWN, and why this conversation needs it (B1470).
   *
   * This pane is live off the WebSocket and needs no beat. But **events sent while the socket is down
   * are not replayed**: when it comes back, whatever was said in the gap is simply not here, and stays
   * missing until the page is reloaded. That is Mike's own sentence -- "his responses do not appear
   * unless I refresh the page" -- arriving in the Cobrain half of this pane from a different cause than
   * it did in the Teams half (B1410).
   *
   * It is not theoretical: EVERY DEPLOY RESTARTS THE API AND DROPS EVERY SOCKET, and there were three
   * deploys in one afternoon on 2026-09-21 alone. Anyone with a chat open across one of them lost what
   * was sent in the reconnect window.
   *
   * The phone app has had this since B1266; the portal did not. `onState` here reports open and closed
   * and does NOT say whether an open is a RE-open, so the "was it ever down" is tracked here: a first
   * connection must not re-read, because the load that opened this conversation just did.
   */
  const onSocketState = useMemo(() => reReadOnReconnect(() => { void load(); }), [load]);
  useMessengerSocket(store, onEvent, onSocketState);
  // And the case the socket cannot report: one the operating system left open while the machine slept,
  // so it never fired a close and never fired an open. Coming back to the window is the signal then.
  // No beat -- the socket is this pane's live channel, and live.ts gives no beat to a read nobody timed.
  useLiveRefresh(load);

  const older = async () => {
    const first = msgs[0];
    if (!first) return;
    try { const r = await messenger.messages(store, id, first.id); setMsgs((m) => [...r.messages, ...m]); setHasMore(r.hasMore); } catch (e) { setError(messageOf(e)); }
  };

  const upload = async (file: File | Blob, name: string) => {
    if (viewing) return;
    setBusy(true); setError(null);
    try {
      const r = await messenger.upload(store, id, file, name);
      const preview = (file.type || '').startsWith('image/') ? URL.createObjectURL(file) : null;
      setPending((p) => [...p, { key: r.file.key, name: r.file.name, mimeType: r.file.mimeType, preview }]);
    } catch (e) { setError(messageOf(e)); } finally { setBusy(false); }
  };
  const onPaste = (e: React.ClipboardEvent) => {
    const items = Array.from(e.clipboardData?.items ?? []);
    const image = items.find((i) => i.kind === 'file' && i.type.startsWith('image/'));
    if (!image) return;
    const f = image.getAsFile();
    if (!f) return;
    e.preventDefault();
    void upload(f, f.name && f.name !== 'image.png' ? f.name : `screenshot-${new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19)}.png`);
  };

  const send = async () => {
    if (viewing) return;
    const body = text.trim();
    if (!body && pending.length === 0) return;
    setBusy(true); setError(null);
    try {
      if (editing) {
        const r = await messenger.edit(store, editing.id, body);
        setMsgs((m) => m.map((x) => (x.id === r.message.id ? r.message : x)));
        setEditing(null);
      } else {
        const r = await messenger.send(store, id, { body, quotedMessageId: quoting?.id ?? null, files: pending.map((p) => ({ key: p.key, name: p.name })) });
        setMsgs((m) => (m.some((x) => x.id === r.message.id) ? m : [...m, r.message]));
        setQuoting(null); setPending([]);
        onChanged();
      }
      setText('');
    } catch (e) { setError(messageOf(e)); } finally { setBusy(false); }
  };
  const remove = async (m: MessageView) => {
    if (!window.confirm('Remove this message from the chat? It stays in the record.')) return;
    try { const r = await messenger.remove(store, m.id); setMsgs((all) => all.map((x) => (x.id === m.id ? { ...x, deletedAt: r.deletedAt, body: '', files: [] } : x))); } catch (e) { setError(messageOf(e)); }
  };
  const answer = async (a: 'accept' | 'dismiss') => {
    setProposalBusy(true);
    try { const r = await messenger.answer(store, id, a); setConv((c) => (c ? { ...c, link: r.link } : c)); } catch (e) { setError(messageOf(e)); } finally { setProposalBusy(false); }
  };

  if (!conv) return <div className="chat-view"><p className="note" style={{ padding: 'calc(var(--pad-x) + var(--r-sm))' }}>{error ?? 'Opening…'}</p></div>;
  const proposal = conv.link && conv.link.proposedBy === 'model' && !conv.link.confirmedAt ? conv.link : null;
  const others = conv.participants.filter((p) => !(p.principalId && p.principalId === me.principalId) && !(p.guestId && p.guestId === me.guestId));
  return (
    <div className="chat-view">
      <div className="chat-head">
        {onBack ? <ActionButton reading type="button" className="dotted chat-back" onClick={onBack}>‹ Chats</ActionButton> : null}
        <b>{conv.name}</b>
        {conv.kind === 'group' ? <span className="note">· {others.length + 1} people{conv.isExternal ? ' · external' : ''}</span> : null}
        <LinkLine link={conv.link} canEdit={canManage} onChange={() => setPanel(panel === 'link' ? 'none' : 'link')} />
        {canManage && conv.isExternal ? <ActionButton type="button" className="dotted" style={{ marginLeft: 'auto' }} onClick={() => setPanel(panel === 'guests' ? 'none' : 'guests')}>Guests{guests.filter((g) => !g.revokedAt).length ? ` (${guests.filter((g) => !g.revokedAt).length})` : ''}</ActionButton> : null}
      </div>
      {panel === 'link' && canManage ? <LinkPanel store={store} conversationId={id} current={conv.link} laneAvailable={laneAvailable} onClose={() => setPanel('none')} onChanged={(l) => setConv((c) => (c ? { ...c, link: l } : c))} /> : null}
      {panel === 'guests' && canManage ? <InviteGuest store={store} conversationId={id} guests={guests} onClose={() => setPanel('none')} onInvited={() => void load()} onRevoked={() => void load()} /> : null}
      <div className="chat-messages" ref={listRef}>
        {hasMore ? <p className="note" style={{ textAlign: 'center' }}><ActionButton reading type="button" className="dotted" onClick={() => void older()}>Earlier messages</ActionButton></p> : null}
        {msgs.map((m, i) => (
          <div key={m.id} style={{ display: 'contents' }}>
            {i === 0 || !sameDay(msgs[i - 1]!.sentAt, m.sentAt) ? <p className="chat-day">{dayWords(m.sentAt)}</p> : null}
            <Bubble m={m} store={store} names={names} onQuote={(x) => { setQuoting(x); setEditing(null); }} onEdit={(x) => { setEditing(x); setQuoting(null); setText(x.body); }} onDelete={(x) => void remove(x)} />
          </div>
        ))}
        {msgs.length === 0 ? <p className="note">Nothing said yet.</p> : null}
        {proposal && canManage ? <Proposal link={proposal} busy={proposalBusy} onAnswer={(a) => void answer(a)} /> : null}
      </div>
      {error ? <p className="note chat-error" role="alert">{error}</p> : null}
      <div className="chat-compose">
        {quoting ? <p className="qr compose-quote">Replying to {quoting.senderName}: {quoting.body.slice(0, 120)} <ActionButton reading type="button" className="dotted" onClick={() => setQuoting(null)}>×</ActionButton></p> : null}
        {editing ? <p className="qr compose-quote">Editing your message <ActionButton reading type="button" className="dotted" onClick={() => { setEditing(null); setText(''); }}>cancel</ActionButton></p> : null}
        {pending.length > 0 ? (
          <div className="compose-files">
            {pending.map((p) => <span key={p.key} className="compose-file">{p.preview ? <img src={p.preview} alt={p.name} /> : null}{p.name} <ActionButton reading type="button" className="dotted" onClick={() => setPending((all) => all.filter((x) => x.key !== p.key))}>×</ActionButton></span>)}
          </div>
        ) : null}
        <div className="compose-row">
          <span className="field wide">
            <ActionTextarea ref={composerRef}
              value={text} rows={1} placeholder={`Message ${conv.name}…`} onPaste={onPaste}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); } if (e.key === 'Escape') { setQuoting(null); setEditing(null); setText(''); } }}
            />
          </span>
          <EmojiButton disabled={busy || !!viewing} onOpen={() => {
            const box = composerRef.current;
            if (box) emojiCursor.current = { start: box.selectionStart, end: box.selectionEnd };
          }} onPick={emoji => {
            if (busy || viewing) return;
            const { start, end } = emojiCursor.current;
            flushSync(() => setText(text.slice(0, start) + emoji + text.slice(end)));
            emojiCursor.current = { start: start + emoji.length, end: start + emoji.length }; focusComposer();
          }} onClose={focusComposer} />
          <ActionInput ref={fileRef} type="file" style={{ display: 'none' }} onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f, f.name); e.target.value = ''; }} />
          <ActionButton type="button" className="btn small quiet" title="Attach a file or image (or paste a screenshot)" disabled={busy} onClick={() => fileRef.current?.click()}>image · file</ActionButton>
          <ActionButton type="button" className="btn small" disabled={busy || (!text.trim() && pending.length === 0)} onClick={() => void send()}>{editing ? 'Save' : 'Send'}</ActionButton>
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------------------------
// The pane, and the page
// -------------------------------------------------------------------------------------------

/** The two-pane messenger. `store` null and `canManage` false is the guest's view (MessengerGuest.tsx). */
export function MessengerPane({ store, selected, onOpen, canManage, title }: { store: string | null; selected: string | null; onOpen: (id: string | null) => void; canManage: boolean; title?: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [rows, setRows] = useState<ConversationRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const refresh = useCallback(() => {
    messenger.conversations(store).then((r) => { setMe(r.me); setRows(r.conversations); setError(null); }).catch((e: unknown) => setError(messageOf(e)));
  }, [store]);
  useEffect(refresh, [refresh]);
  useLiveRefresh(refresh);
  useMessengerSocket(store, (e) => { if (e.type === 'message' || e.type === 'conversation' || e.type === 'read' || e.type === 'delete') refresh(); });
  const narrow = typeof window !== 'undefined' && window.innerWidth < 760;
  return (
    <div className="chat-page">
      {title}
      {error ? <p className="note chat-error" role="alert">{error} <ActionButton reading type="button" className="dotted" onClick={refresh}>Try again</ActionButton></p> : null}
      <div className={selected && narrow ? 'chat-frame one' : 'chat-frame'}>
        {!(selected && narrow) ? <ChatList rows={rows} selected={selected} onOpen={(id) => { setCreating(false); onOpen(id); }} onNew={() => setCreating(true)} canStart={canManage} /> : null}
        {creating && canManage ? (
          <div className="chat-view"><NewChat store={store} onClose={() => setCreating(false)} onMade={(c) => { setCreating(false); refresh(); onOpen(c.id); }} /></div>
        ) : selected && me ? (
          <ChatView key={selected} store={store} me={me} id={selected} onChanged={refresh} canManage={canManage} {...(narrow ? { onBack: () => onOpen(null) } : {})} />
        ) : (
          <div className="chat-view"><p className="note" style={{ padding: 'calc(var(--pad-x) + var(--r-sm))' }}>{rows.length ? 'Pick a chat.' : canManage ? 'Start a chat with a colleague, or a group; guests join an external group by a link you send them.' : 'Nothing here yet.'}</p></div>
        )}
      </div>
    </div>
  );
}

/** The signed-in page: /chats and /chats/:id (App.tsx's route). */
export function Messenger({ store, id, go }: { store: Store; id?: string; go: (path: string) => void }) {
  return (
    <MessengerPane
      store={store.tenantId} selected={id ?? null} canManage
      onOpen={(next) => go(next ? `/chats/${next}` : '/chats')}
      title={<SectionHeading title={<>Chats</>} note={<>flat, quote to reply; every message is in the record</>} />}
    />
  );
}
